import { TooltipIconButton } from '@if/mui'
import { IconButtonTypeMap, SxProps, Theme, TooltipProps } from '@mui/material'
import { ExtendButtonBase } from '@mui/material/ButtonBase'
import { CSSProperties, ElementType, ReactElement, Ref, forwardRef } from 'react'

interface IconButtonWithTooltipExtras {
  tooltip: string
  icon: ReactElement
  visibility?: CSSProperties['visibility']
  /**
   * CSS color string applied via `sx`. Kept for backwards compatibility with
   * call sites that pass raw colors (e.g. `yellow[600]`, `'#FFC107'`).
   * Prefer `sx={{ color: '…' }}` in new code.
   */
  color?: CSSProperties['color']
}

// Pick — not Omit — to avoid pulling in HTMLAttributes<HTMLDivElement> from
// TooltipProps, which collides with IconButton's HTMLButtonElement-typed events.
type TooltipExtras = Pick<
  TooltipProps,
  | 'title'
  | 'arrow'
  | 'placement'
  | 'describeChild'
  | 'disableFocusListener'
  | 'disableHoverListener'
  | 'disableInteractive'
  | 'disableTouchListener'
  | 'enterDelay'
  | 'enterNextDelay'
  | 'enterTouchDelay'
  | 'leaveDelay'
  | 'leaveTouchDelay'
  | 'followCursor'
  | 'open'
  | 'onOpen'
  | 'onClose'
  | 'slots'
  | 'slotProps'
>

export interface IconButtonWithTooltipTypeMap<
  AdditionalProps = object,
  RootComponent extends ElementType = 'button',
> {
  props: Omit<
    IconButtonTypeMap<AdditionalProps & TooltipExtras, RootComponent>['props'],
    'title' | 'children' | 'color'
  > &
    IconButtonWithTooltipExtras
  defaultComponent: RootComponent
}

const Impl = forwardRef<HTMLElement, IconButtonWithTooltipTypeMap['props']>(
  ({ tooltip, icon, visibility = 'visible', color, sx, ...rest }, ref) => (
    <TooltipIconButton
      ref={ref as Ref<HTMLButtonElement>}
      arrow
      placement="top"
      {...rest}
      title={tooltip}
      disableHoverListener={visibility === 'hidden'}
      sx={[{ visibility, color }, ...((Array.isArray(sx) ? sx : [sx]) as SxProps<Theme>[])] as SxProps<Theme>}
    >
      {icon}
    </TooltipIconButton>
  ),
)
Impl.displayName = 'IconButtonWithTooltip'

const IconButtonWithTooltip = Impl as unknown as ExtendButtonBase<IconButtonWithTooltipTypeMap>

export default IconButtonWithTooltip
